import type { ZyServers } from './servers-core';

export interface S3Object {
    key: string;
    last_modified: string;
    metadata: Record<string, string>;
}

export interface LtiLaunchDataRequest {
    providerName: string;
    resourceLinkId: string;
    zybookCode: string | null;
    contentResourceId: unknown;
}

export interface LtiLaunchData {
    launch_data?: Record<string, unknown>;
    launch_url?: string;
}

/**
 * Creates the zyBooks API calls shared by the browser app and the Lambda.
 * Functions close over `servers`, so they are safe to destructure.
 * @param servers - Server clients to call
 * @returns The bound API functions
 */
export function createZybooksApi(servers: ZyServers) {
    /**
     * Colorize code using the zyServer API
     * @returns Colorized HTML, or the original code for plaintext or on any failure
     */
    const colorizeCode = async (code: string, language: string, highlight?: string, numbering?: boolean): Promise<string> => {
        if (!language || language === 'plaintext' || language === 'text') {
            return code;
        }

        try {
            const data = await servers.zyServer.post<{ success?: boolean; colorized_code?: string }>('code_colorize', {
                body: {
                    plain_text_code: code,
                    language,
                    hl_lines: highlight,
                    linenos: numbering,
                },
            });
            if (data.success && data.colorized_code) {
                return data.colorized_code;
            }
        } catch (error) {
            console.error('Error colorizing code:', error);
        }

        return code;
    };

    const uploadGdocDrawingToS3 = async (
        fileId: string,
        nodeId: string,
        subject: string,
        releaseID: string,
        gdocModifiedTime: string,
    ): Promise<S3Object> => {
        const response = await servers.zyAuthor.post<{ image_key_and_last_modified_and_metadata: S3Object }>(
            'modern_authoring/upload_gdoc_drawing',
            {
                body: {
                    file_id: fileId,
                    node_id: nodeId,
                    subject,
                    releaseID,
                    gdoc_modified_time: gdocModifiedTime,
                },
            },
        );
        return response.image_key_and_last_modified_and_metadata;
    };

    const getDriveImagesAndMetadata = (imageIds: string[]): Promise<Record<string, unknown>> =>
        servers.zyAuthor.post('get_drive_images_and_metadata', { body: { image_ids: imageIds } });

    const generateLtiLaunchData = (request: LtiLaunchDataRequest): Promise<LtiLaunchData> =>
        servers.zyServer.post('lti/generate_launch_data', {
            body: {
                provider_name: request.providerName,
                resource_link_id: request.resourceLinkId,
                is_lab: false,
                is_zml: false,
                is_ma_preview: true,
                zybook_code: request.zybookCode,
                content_resource_id: request.contentResourceId,
            },
        });

    return { colorizeCode, uploadGdocDrawingToS3, getDriveImagesAndMetadata, generateLtiLaunchData };
}

export type ZybooksApi = ReturnType<typeof createZybooksApi>;
